import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import { Await } from 'react-router-dom';
import { TeacherInfo } from '../../../types/TeacherInfo';
import { useReservationContext } from '../../../providers/ReservationProvider';
import { trackClickButtonEvent } from '../../../utils/functions/trackEvent';
import { CalendarInfo } from '../../../types/CalendarInfo';
import Loader from '../../Loader';

// Lazy loaded components
const ReservationCalendar = React.lazy(() => import('./ReservationCalendar'));
const ReservationCheckout = React.lazy(() => import('./ReservationCheckout'));
const ReservationPayment = React.lazy(() => import('./ReservationPayment'));
const ReservationPaymentConfirmation = React.lazy(() => import('./ReservationPaymentConfirmation'));

type ReservationProps = {
  calendarData: Promise<CalendarInfo>;
  teacher: TeacherInfo;
  initialStep?: 'calendar' | 'checkout' | 'payment' | 'payment_confirmation';
};

interface SelectedSlot {
  start: Date;
  startStr: String;
  end: Date;
  endStr: String;
}

const ReservationSkeleton = ({ height }: { height: number }) => (
  <Skeleton
    variant="rounded"
    animation="pulse"
    width="100%"
    height={height}
    sx={{ marginTop: '2%' }}
  />
);

const Reservation: React.FC<ReservationProps> = ({
  calendarData,
  teacher,
  initialStep = 'calendar'
}) => {
  const { clearContext, reservationEvent } = useReservationContext();
  const [selectedCalendarSlot, setSelectedCalendarSlot] = useState<SelectedSlot | null>(null);
  const [step, setStep] = useState<'calendar' | 'checkout' | 'payment' | 'payment_confirmation'>(
    initialStep
  );

  // Sync internal step with initialStep prop
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  // Set selected calendar slot from reservation event if available
  useEffect(() => {
    if (reservationEvent && (step === 'payment' || step === 'payment_confirmation')) {
      setSelectedCalendarSlot({
        start: reservationEvent.start,
        startStr: reservationEvent.start.toISOString(),
        end: reservationEvent.end,
        endStr: reservationEvent.end.toISOString()
      });
    }
  }, [reservationEvent, step]);

  const handleCalendarReserve = useCallback(
    (selectedSlot: any) => {
      trackClickButtonEvent('calendar_slot_confirmation');
      setSelectedCalendarSlot(selectedSlot);
      clearContext(); //FixMe: remove this clear context and fix error handling.
    },
    [clearContext]
  );

  const handleBack = useCallback(() => {
    setStep('calendar');
    setSelectedCalendarSlot(null);
  }, []);

  const handleSubmitButton = useCallback(async () => {
    setStep('payment');
  }, []);

  const handleCalendarSubmit = useCallback(() => {
    setStep('checkout');
  }, []);

  const handlePaymentSubmit = useCallback(() => {
    setStep('payment_confirmation');
  }, []);

  // Memoize the reservation string to avoid unnecessary re-renders
  const reservationString = useMemo(
    () => (teacher.reservation_type === 'purchase' ? 'Compra' : 'Reserva'),
    [teacher.reservation_type]
  );

  return (
    <React.Suspense fallback={<ReservationSkeleton height={step === 'checkout' ? 900 : 800} />}>
      <Await resolve={calendarData} errorElement={<p>Ocurrió un error cargando el calendario!</p>}>
        {() => {
          return (
            <React.Fragment>
              {step === 'calendar' && (
                <ReservationCalendar
                  teacherProfile={teacher}
                  onSlotSelected={handleCalendarReserve}
                  onSubmit={handleCalendarSubmit}
                />
              )}

              {step === 'checkout' && selectedCalendarSlot && (
                <ReservationCheckout
                  onBack={handleBack}
                  onSubmit={handleSubmitButton}
                  teacher={teacher}
                  start={selectedCalendarSlot.start}
                  end={selectedCalendarSlot.end}
                  reservationType={reservationString}
                />
              )}

              {step === 'payment' && !selectedCalendarSlot && (
                <Loader variant="clock" text="Cargando información de pago..." />
              )}
              {step === 'payment' && selectedCalendarSlot && (
                <ReservationPayment
                  key={`payment-${selectedCalendarSlot.start.getTime()}`}
                  onBack={handleBack}
                  onSubmit={handlePaymentSubmit}
                  teacher={teacher}
                  start={selectedCalendarSlot.start}
                  end={selectedCalendarSlot.end}
                  reservationType={reservationString}
                />
              )}

              {step === 'payment_confirmation' && !selectedCalendarSlot && (
                <Loader variant="clock" text="Cargando confirmación de la reserva..." />
              )}
              {step === 'payment_confirmation' && selectedCalendarSlot && (
                <ReservationPaymentConfirmation
                  onBack={handleBack}
                  teacher={teacher}
                  start={selectedCalendarSlot.start}
                  end={selectedCalendarSlot.end}
                  reservationType={reservationString}
                />
              )}
            </React.Fragment>
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export default Reservation;
